import { AxiosError } from 'axios';
import { useRouter } from 'next/router';
import { useMutation, useQueryClient } from 'react-query';
import { ServerStateKeysEnum } from 'reactQuery/constants';
import { checkoutService } from 'services';
import { Cart, IOrder, PlaceOrderParams } from 'types';
import { pushToGTM } from 'utils';

export const usePlaceOrder = () => {
  const queryClient = useQueryClient();
  const router = useRouter();
  return useMutation(
    ({
      selectedCreditCard,
      isPayByInvoiceSelected,
      setOrderStep,
      setOrderFailure,
      ipAddress,
      pcUsername,
    }: PlaceOrderParams) => {
      return checkoutService.placeOrder(
        selectedCreditCard,
        isPayByInvoiceSelected,
        ipAddress,
        pcUsername
      );
    },
    {
      onSuccess: (data, variables, context) => {
        variables.setOrderStep('complete');
        const userCurrentCart = queryClient.getQueryData<Cart>(
          ServerStateKeysEnum.userCurrentCart
        );

        pushPurchaseEventToGTM(userCurrentCart, data);
        router.push(
          `/orderdetails/${data.data.attributes.number}?checkoutState=complete`
        );

        queryClient.invalidateQueries([ServerStateKeysEnum.userCurrentCart]);
        queryClient.invalidateQueries([ServerStateKeysEnum.orderHistory]);

        queryClient.invalidateQueries([
          ServerStateKeysEnum.subscriptionHistory,
        ]);
      },
      onError: (
        error: AxiosError<{
          error?: { stock_error?: boolean; message: string };
        }>,
        variables
      ) => {
        const stockError = error.response?.data?.error?.stock_error;
        if (stockError) {
          variables.setOrderStep('payment');
        } else if (error.response?.status === 422) {
          variables.setOrderFailure('CREDIT_CARD_ERROR');
          variables.setOrderStep('payment');
        } else {
          variables.setOrderFailure('GENERIC_ORDER_ERROR');
        }
      },
    }
  );
};

const pushPurchaseEventToGTM = (
  userCurrentCart: Cart | undefined,
  data: IOrder
) => {
  const orderDetails = data.data.attributes;
  const orderLineItems: {
    item_id: string;
    item_name: string;
    affiliation: string;
    currency: string;
    price: string;
    quantity: number;
  }[] = [];

  if (userCurrentCart) {
    userCurrentCart.lineItems.forEach(lineItem => {
      orderLineItems.push({
        item_id: lineItem.slug,
        item_name: lineItem.name,
        affiliation: 'Global Store NA',
        currency: orderDetails.currency,
        price: parseFloat(`${lineItem.price}`).toFixed(2),
        quantity: lineItem.quantity,
      });
    });
  }

  pushToGTM('purchase', {
    ecommerce: {
      transaction_id: orderDetails.number,
      affiliation: 'Global Store NA',
      value: Number(orderDetails.item_total),
      tax: Number(orderDetails.tax_amount),
      shipping: Number(orderDetails.ship_total),
      currency: orderDetails.currency,
      items: orderLineItems,
    },
  });
};
